<template>
  <el-dialog
      title="上传会后材料"
      :visible.sync="visible"
      width="600px"
      custom-class="upload-material-dialog"
      @close="handleClose"
  >
    <!-- 案例信息（固定，由父组件传入） -->
    <div class="section">
      <label class="section-label">关联案例</label>
      <div class="case-card">
        <div class="card-row">
          <i class="el-icon-document card-icon"></i>
          <span>{{ caseInfo.caseTitle || '—' }}</span>
        </div>
        <div class="card-row">
          <i class="el-icon-s-data card-icon"></i>
          <span>编号：{{ caseInfo.caseNo || '—' }}</span>
        </div>
        <div class="card-row">
          <i class="el-icon-user card-icon"></i>
          <span>提交人：{{ caseInfo.submitter || '—' }}</span>
        </div>
      </div>
    </div>

    <!-- 会议选择（用户必须选） -->
    <div class="section">
      <label class="section-label">请选择会议 <span style="color: red;">*</span></label>
      <el-select
          v-model="selectedMeetingId"
          placeholder="请选择会议"
          style="width: 100%;"
          @change="handleChangeMeeting"
      >
        <el-option
            v-for="meeting in meetings"
            :key="meeting.id"
            :label="meeting.title"
            :value="meeting.id"
        />
      </el-select>
    </div>

    <!-- 会议详情卡片（选中后显示） -->
    <div v-if="selectedMeeting" class="meeting-card">
      <div class="card-row">
        <i class="el-icon-time card-icon"></i>
        <span>{{ selectedMeeting.time }}</span>
      </div>
      <div class="card-row">
        <i class="el-icon-user card-icon"></i>
        <span>主持人：{{ selectedMeeting.host }}</span>
      </div>
      <div class="card-row">
        <i class="el-icon-video-camera card-icon"></i>
        <span>会议号：{{ selectedMeeting.meetingId }}</span>
      </div>
      <div class="card-hint">
        <i class="el-icon-warning-outline"></i>
        <span>请在会议开始前至少24小时上传材料，支持 PDF / Word / PPT / 图片，单个文件 ≤50MB。</span>
      </div>
    </div>

    <!-- 上传区域 -->
    <div class="section">
      <label class="section-label">上传材料包</label>
      <el-upload
          ref="upload"
          :auto-upload="false"
          :on-change="handleChangeFile"
          :on-remove="handleRemove"
          :file-list="fileList"
          :limit="5"
          :accept="'.pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png'"
          drag
          multiple
          :show-file-list="true"
          :before-upload="beforeUpload"
          :on-exceed="handleExceed"
          class="upload-area"
      >
        <i class="el-icon-upload upload-icon"></i>
        <div class="el-upload__text">点击或拖拽文件至此处上传</div>
        <div class="upload-tip">支持 PDF、Word、PPT、图片格式，单个文件不超过 50MB</div>
      </el-upload>
    </div>

    <!-- 已上传文件列表（显式展示） -->
    <div v-if="fileList.length > 0" class="section">
      <label class="section-label">已上传文件</label>
      <div class="uploaded-list">
        <div v-for="(file, index) in fileList" :key="index" class="uploaded-item">
          <i class="el-icon-document" style="color: #409EFF;"></i>
          <span class="filename">{{ file.name }}</span>
          <span class="filesize">({{ formatFileSize(file.size) }})</span>
        </div>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="visible = false">取消</el-button>
      <el-button
          type="primary"
          @click="submitFiles"
          :loading="submitting"
          :disabled="!selectedMeetingId || fileList.length === 0"
      >
        确认上传
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'UploadMaterialDialog',
  props: {
    // 父组件传入当前案例信息
    caseInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      visible: false,
      submitting: false,
      selectedMeetingId: null,
      selectedMeeting: null,
      meetings: [
        {
          id: 1,
          title: '糖尿病治疗研讨会（10月）',
          time: '2023-10-25 14:00–16:00',
          host: '张教授（北京协和医院）',
          meetingId: '987-654-321'
        },
        {
          id: 2,
          title: '高血压管理专题会（11月）',
          time: '2023-11-10 10:00–12:00',
          host: '李主任（上海瑞金医院）',
          meetingId: '123-456-789'
        }
      ],
      fileList: [],
      maxSize: 50 * 1024 * 1024 // 50MB
    };
  },
  methods: {
    // 父组件调用此方法打开弹窗
    open() {
      this.visible = true;
      this.selectedMeetingId = null;
      this.selectedMeeting = null;
      this.fileList = [];
    },

    handleChangeMeeting(id) {
      this.selectedMeeting = this.meetings.find(m => m.id === id);
    },

    handleChangeFile(file, fileList) {
      this.fileList = fileList;
    },

    handleRemove(file, fileList) {
      this.fileList = fileList;
    },

    handleExceed() {
      this.$message.warning('最多可上传 5 个文件');
    },

    beforeUpload(file) {
      const validExt = /\.(pdf|doc|docx|ppt|pptx|jpg|jpeg|png) $ /i;
      if (!validExt.test(file.name)) {
        this.$message.error('仅支持 PDF、Word、PPT、图片格式！');
        return false;
      }
      if (file.size > this.maxSize) {
        this.$message.error('文件大小不能超过 50MB！');
        return false;
      }
      return true;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async submitFiles() {
      if (!this.selectedMeetingId) {
        this.$message.warning('请选择会议');
        return;
      }
      if (this.fileList.length === 0) {
        this.$message.warning('请先上传文件');
        return;
      }

      this.submitting = true;

      try {
        // 模拟上传 API 调用
        await new Promise(resolve => setTimeout(resolve, 800));

        // 实际项目中这里应调用接口：
        // await this. $ api.uploadMaterials({
        //   caseId: this.caseInfo.id,
        //   meetingId: this.selectedMeetingId,
        //   files: this.fileList
        // });

        this.$message.success('材料上传成功！');
        this.visible = false;
        this.$emit('success', {
          caseId: this.caseInfo.id,
          meetingId: this.selectedMeetingId,
          files: this.fileList
        });
      } catch (error) {
        this.$message.error('上传失败，请重试');
      } finally {
        this.submitting = false;
      }
    },

    handleClose() {
      this.visible = false;
      this.fileList = [];
      this.selectedMeetingId = null;
      this.selectedMeeting = null;
    }
  }
};
</script>

<style scoped>
.section {
  margin-bottom: 24px;
}

.section-label {
  display: block;
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
  font-weight: 500;
}

.case-card,
.meeting-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #ebeef5;
}

.card-row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  font-size: 13px;
  color: #495060;
}
.card-row:last-child {
  margin-bottom: 0;
}
.card-icon {
  color: #909399;
  margin-right: 8px;
  font-size: 14px;
}

.card-hint {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  background: #ecf5ff;
  border-radius: 4px;
  font-size: 12px;
  color: #5d86b9;
}
.card-hint i {
  margin-right: 6px;
  margin-top: 2px;
  font-size: 14px;
}

.upload-area {
  text-align: center;
  margin-bottom: 16px;
}
.upload-icon {
  font-size: 48px;
  color: #c0c4cc;
  margin-bottom: 12px;
}
.el-upload__text {
  font-size: 14px;
  color: #606266;
}
.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

/* 已上传文件列表 */
.uploaded-list {
  background: #f5f7fa;
  border-radius: 6px;
  padding: 12px;
}
.uploaded-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
}
.uploaded-item:last-child {
  margin-bottom: 0;
}
.filename {
  margin-left: 8px;
  color: #409eff;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.filesize {
  color: #909399;
  font-size: 12px;
}

.dialog-footer {
  text-align: right;
}
</style>
